import React, { useCallback, useEffect, useState } from 'react';

import { listStoreProductPlans, setStoreProductPlan, type StoreProductPlan } from './api';

/**
 * Store products.
 *
 * The App Store and Google Play set a product's id and price; this says which
 * plan and how many assets that product grants when somebody buys it in the
 * app. The product id must match App Store Connect / Play Console exactly, and
 * what the app offers is the RevenueCat offering, not this list.
 */

const BLANK: StoreProductPlan = { product_id: '', plan_key: 'starter', max_assets: null, term: 'monthly', is_active: true };

function Row({
  row,
  plans,
  isNew,
  onSave,
}: {
  row: StoreProductPlan;
  plans: { plan_key: string; display_name: string }[];
  isNew: boolean;
  onSave: (row: StoreProductPlan) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState(row);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(row), [row]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);
  const set = (patch: Partial<StoreProductPlan>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <tr style={draft.is_active ? undefined : { opacity: 0.6 }}>
      <td className="feat-name">
        {isNew ? (
          <input
            value={draft.product_id}
            placeholder="assetwrx_solo_5_monthly"
            onChange={(e) => set({ product_id: e.target.value })}
            style={{ width: 230 }}
          />
        ) : (
          <code>{row.product_id}</code>
        )}
      </td>
      <td>
        <select value={draft.plan_key} onChange={(e) => set({ plan_key: e.target.value })}>
          {plans.map((p) => (
            <option key={p.plan_key} value={p.plan_key}>{p.display_name}</option>
          ))}
        </select>
      </td>
      <td>
        <input
          type="number"
          min={1}
          value={draft.max_assets ?? ''}
          placeholder="Plan limit"
          onChange={(e) => set({ max_assets: e.target.value === '' ? null : Math.max(1, Math.trunc(Number(e.target.value))) })}
          style={{ width: 110 }}
        />
      </td>
      <td>
        <select value={draft.term} onChange={(e) => set({ term: e.target.value as StoreProductPlan['term'] })}>
          <option value="monthly">Monthly</option>
          <option value="annual">Annual</option>
        </select>
      </td>
      <td>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={draft.is_active} onChange={(e) => set({ is_active: e.target.checked })} />
          {draft.is_active ? 'Selling' : 'Off'}
        </label>
      </td>
      <td style={{ textAlign: 'right' }}>
        <button
          className="btn primary"
          disabled={!dirty || saving || !draft.product_id.trim()}
          onClick={async () => {
            setSaving(true);
            const ok = await onSave(draft);
            setSaving(false);
            if (ok && isNew) setDraft(BLANK);
          }}
        >
          {saving ? 'Saving…' : isNew ? 'Add' : 'Save'}
        </button>
      </td>
    </tr>
  );
}

export default function StoreProducts({
  plans,
  onBack,
  toast,
}: {
  /** Paid plans from the live price list, with their names. */
  plans: { plan_key: string; display_name: string }[];
  onBack: () => void;
  toast: (message: string) => void;
}) {
  const [rows, setRows] = useState<StoreProductPlan[] | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await listStoreProductPlans());
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
      setRows([]);
    }
  }, [toast]);
  useEffect(() => void load(), [load]);

  const save = async (row: StoreProductPlan) => {
    try {
      await setStoreProductPlan(row);
      toast(`Saved ${row.product_id.trim()}.`);
      await load();
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
      return false;
    }
  };

  const paid = plans.filter((p) => p.plan_key !== 'free');

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <p className="lede">What each in-app purchase grants.</p>
          <p className="sub" style={{ margin: '4px 0 0' }}>
            The App Store and Google Play set the product id and the price. This sets the plan and
            the assets a buyer gets. The id must match the store exactly. Switch a product off
            instead of removing it: people who already bought it keep renewing on it.
          </p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={onBack}>Back to prices</button>
        </div>
      </div>

      {rows === null ? (
        <p className="muted">Loading store products…</p>
      ) : (
        <div className="card">
          <table className="matrix">
            <thead>
              <tr>
                <th>Store product id</th><th>Plan</th><th>Assets</th><th>Billed</th><th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.product_id} row={r} plans={paid} isNew={false} onSave={save} />
              ))}
              <Row row={BLANK} plans={paid} isNew onSave={save} />
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
